import { useAuth } from "@/auth";
import { useDedupedEffect } from "@/hooks/use-deduped-effect";
import { queryClient } from "@/utils/api";
import { queryOptions } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import z from "zod";
import { emit, joinRoom, usePushMessageListener } from "./socket.service";

type EditorLockID = string & {};

const HEARTBEAT_INTERVAL_MS = 2 * 1000; // 2 seconds
const INACTIVE_TIME_MS = 5 * 60 * 1000; // 5 minutes

export const editorLockUserSessionSchema = z.object({
  sessionId: z.string(),
  joinTimeMs: z.number(),
});

export type EditorLockUserSession = z.infer<typeof editorLockUserSessionSchema>;

export const editorLockUserSchema = z.object({
  userId: z.string(),
  sessions: z.array(editorLockUserSessionSchema),
});

export type EditorLockUser = z.infer<typeof editorLockUserSchema>;

const editorLockStateSchema = z.object({
  editorLockId: z.string(),
  users: z.array(editorLockUserSchema),
  currentEditor: z
    .object({
      userId: z.string(),
      sessionId: z.string(),
    })
    .nullable(),
});

export type EditorLockState = z.infer<typeof editorLockStateSchema>;

export const editorLockUpdatesSchema = z.array(editorLockStateSchema);

export type EditorLockUpdates = z.infer<typeof editorLockUpdatesSchema>;

const editorLockDeadStateSchema = z.object({
  editorLockId: z.string(),
  isDead: z.literal(true),
});

export type EditorLockDeadState = z.infer<typeof editorLockDeadStateSchema>;

export type EditorLockContextState = {
  sessionId: string;
  isReadyToJoin: boolean;
  editorLocks: Map<EditorLockID, EditorLockState>;
};

export const EditorLockContext = createContext<EditorLockContextState | null>(
  null,
);

export function useEditorLockService() {
  const { user } = useAuth();

  const roomId = useMemo(
    () => user.lassoOrganizationId,
    [user.lassoOrganizationId],
  );

  const [state, setState] = useState<EditorLockContextState>(() => ({
    sessionId: crypto.randomUUID(),
    isReadyToJoin: false,
    editorLocks: new Map(),
  }));

  useDedupedEffect(() => {
    let currentConnectionSymbol = Symbol();

    const resetState = () => {
      setState((prev) => ({
        ...prev,
        isReadyToJoin: false,
        editorLocks: new Map(),
      }));
    };

    const pingQueryOptions = createEditorLockPingQueryOptions({ roomId });

    const leaveRoom = joinRoom(roomId, {
      onConnected: async () => {
        const connectionSymbol = Symbol();
        currentConnectionSymbol = connectionSymbol;

        const updates = await queryClient.ensureQueryData(pingQueryOptions);
        if (connectionSymbol !== currentConnectionSymbol) return;

        setState((prev) => ({
          ...prev,
          isReadyToJoin: true,
          editorLocks: new Map(
            updates.map((update) => [update.editorLockId, update]),
          ),
        }));
      },
      onDisconnected: () => {
        currentConnectionSymbol = Symbol();
        resetState();
        queryClient.invalidateQueries({ queryKey: pingQueryOptions.queryKey });
      },
    });

    return () => {
      currentConnectionSymbol = Symbol();
      leaveRoom();
      resetState();
    };
  }, roomId);

  usePushMessageListener(
    "EditorLockUpdate",
    useCallback((m: { message: unknown }) => {
      const { data } = z
        .union([
          editorLockStateSchema.transform((s) => ({
            ...s,
            isDead: false as const,
          })),
          editorLockDeadStateSchema,
        ])
        .safeParse(m.message);
      if (!data) return;
      setState((prev) => {
        const newEditorLocks = new Map(prev.editorLocks);
        if (data.isDead) {
          newEditorLocks.delete(data.editorLockId);
        } else {
          const { isDead, ...state } = data;
          newEditorLocks.set(data.editorLockId, state);
        }
        return {
          ...prev,
          editorLocks: newEditorLocks,
        };
      });
    }, []),
  );

  return state;
}

export function useEditorLock({
  enabled = true,
  editorLockId,
}: {
  enabled?: boolean;
  editorLockId: EditorLockID;
}) {
  const { user } = useAuth();

  const { lassoOrganizationId: roomId, id: userId } = user;

  const state = useContext(EditorLockContext);
  if (!state) throw new Error("No EditorLockContext found");

  const { sessionId, isReadyToJoin } = state;

  const editorLock = useMemo(
    () => state.editorLocks.get(editorLockId) || null,
    [state, editorLockId],
  );

  const users = useMemo(() => editorLock?.users || [], [editorLock]);

  const currentEditor = useMemo(
    () => editorLock?.currentEditor || null,
    [editorLock],
  );

  const isEditor = useMemo(
    () =>
      !!currentEditor &&
      currentEditor.userId === userId &&
      currentEditor.sessionId === sessionId,
    [currentEditor, sessionId, userId],
  );

  const isEditorInOtherSession = useMemo(
    () =>
      !!currentEditor &&
      currentEditor.userId === userId &&
      currentEditor.sessionId !== sessionId,
    [currentEditor, sessionId, userId],
  );

  const { isActive } = useUserActivity({
    inactiveTimeMs: INACTIVE_TIME_MS,
  });

  useDedupedEffect(
    () => {
      if (!enabled || !isReadyToJoin || !isActive) return;

      let heartbeatIntervalId: number | null = null;

      const clearHeartbeatInterval = () => {
        if (heartbeatIntervalId !== null) {
          window.clearInterval(heartbeatIntervalId);
          heartbeatIntervalId = null;
        }
      };

      const onWindowFocus = () => {
        emit("editor-lock:take-over-user-session", {
          roomId,
          editorLockId,
          userId,
          sessionId,
        });
      };

      let currentOperationSymbol = Symbol();

      const join = () => {
        const operationSymbol = Symbol();
        currentOperationSymbol = operationSymbol;

        clearHeartbeatInterval();

        emit(
          "editor-lock:join",
          {
            roomId,
            editorLockId,
            userId,
            sessionId,
          },
          () => {
            if (currentOperationSymbol !== operationSymbol) {
              return;
            }

            heartbeatIntervalId = window.setInterval(() => {
              if (currentOperationSymbol !== operationSymbol) {
                return;
              }

              emit("editor-lock:heartbeat", {
                roomId,
                editorLockId,
                userId,
                sessionId,
              });
            }, HEARTBEAT_INTERVAL_MS);

            window.addEventListener("focus", onWindowFocus);
          },
        );
      };

      const leave = () => {
        const operationSymbol = Symbol();
        currentOperationSymbol = operationSymbol;

        clearHeartbeatInterval();

        window.removeEventListener("focus", onWindowFocus);

        emit("editor-lock:leave", {
          roomId,
          editorLockId,
          userId,
          sessionId,
        });
      };

      join();

      const events = [["beforeunload", () => leave()]] as const;

      events.forEach(([event, fn]) => window.addEventListener(event, fn));

      return () => {
        events.forEach(([event, fn]) => window.removeEventListener(event, fn));

        leave();
      };
    },
    useMemo(
      () =>
        enabled && isReadyToJoin && isActive
          ? [roomId, editorLockId, userId, sessionId].join("|")
          : "",
      [
        enabled,
        isReadyToJoin,
        isActive,
        roomId,
        editorLockId,
        userId,
        sessionId,
      ],
    ),
  );

  return useMemo(
    () => ({
      userId,
      sessionId,
      users,
      currentEditor,
      isEditor,
      isEditorInOtherSession,
    }),
    [currentEditor, isEditor, isEditorInOtherSession, sessionId, userId, users],
  );
}

export const createEditorLockPingQueryOptions = ({
  roomId,
}: {
  roomId: string;
}) =>
  queryOptions({
    queryKey: ["editorLockPing", roomId] as const,
    queryFn: () =>
      new Promise<EditorLockUpdates>((resolve) => {
        emit("editor-lock:ping", { roomId }, (result: unknown) => {
          const updates = editorLockUpdatesSchema.parse(result);
          resolve(updates);
        });
      }),
    staleTime: Infinity,
  });

/**
 * A hook to track user activity on the page.
 */
const useUserActivity = ({ inactiveTimeMs }: { inactiveTimeMs: number }) => {
  const lastActivityTimeRef = useRef(Date.now());

  const [isActive, setIsActive] = useState(true);
  const [isWindowFocused, setIsWindowFocused] = useState(true);

  useEffect(() => {
    let inactivityChecker: {
      destroy: () => void;
    } | null = null;

    const startInactivityChecker = async () => {
      if (inactivityChecker) return;

      let destroyed = false;

      const destroy = () => {
        destroyed = true;
        inactivityChecker = null;
      };

      inactivityChecker = {
        destroy,
      };

      while (!destroyed) {
        const timeoutMs =
          lastActivityTimeRef.current + inactiveTimeMs - Date.now();

        if (timeoutMs > 0) {
          await new Promise((resolve) => setTimeout(resolve, timeoutMs));
          if (destroyed) return;
        }

        if (Date.now() - lastActivityTimeRef.current >= inactiveTimeMs) {
          setIsActive(false);
          destroy();
        }
      }
    };

    const updateActivity = (_event?: Event) => {
      lastActivityTimeRef.current = Date.now();
      setIsActive(true);
      startInactivityChecker();
    };

    updateActivity();

    const events = [
      ["pointerdown", updateActivity],
      ["pointermove", updateActivity],
      ["pointerup", updateActivity],
      ["keydown", updateActivity],
      ["keyup", updateActivity],
      ["wheel", updateActivity],
      ["scroll", updateActivity],
      [
        "focus",
        (event: Event) => {
          setIsWindowFocused(true);
          updateActivity(event);
        },
      ],
      [
        "blur",
        () => {
          setIsWindowFocused(false);
        },
      ],
    ] as const;

    events.forEach(([event, fn]) => window.addEventListener(event, fn));

    return () => {
      events.forEach(([event, fn]) => window.removeEventListener(event, fn));

      inactivityChecker?.destroy();
    };
  }, [inactiveTimeMs]);

  return useMemo(
    () => ({ isActive, isWindowFocused }),
    [isActive, isWindowFocused],
  );
};
